/**
 * 角色行内权限资源列表：平铺数组，通过 id / parentId 表示父子，展示用 name。
 * 支持三层：根（L1）→ 中间节点（L2）→ 叶子（L3，以 Tag 展示）。
 */

function refKey(v) {
  if (v === null || v === undefined || v === '') return null
  return String(v)
}

function isRootNode(n, idSet) {
  const pid = n.parentId
  const pKey = refKey(pid)
  const isZeroRoot =
    pid === null || pid === undefined || pid === '' || pKey === '0'
  const parentMissing = !isZeroRoot && pKey != null && !idSet.has(pKey)
  return isZeroRoot || parentMissing
}

/**
 * @param {object} row 表格行
 * @returns {Array<{ id: number|string, parentId?: number|string|null, name: string }>}
 */
export function pickRolePermissionNodes(row) {
  if (!row || typeof row !== 'object') return []
  const raw = row.permissions ?? row.permissionList ?? row.resources
  if (!Array.isArray(raw)) return []
  return raw.filter((n) => n && typeof n === 'object' && n.id != null && n.name != null)
}

/**
 * 角色行内权限往往只含已选项（可能缺父节点）。用全量权限平铺按 parentId 向上补祖先，供列表「权限」列正确三层分组。
 * @param {Array<object>} roleNodes pickRolePermissionNodes 结果
 * @param {Array<object>} flatAll GET /system/permissions 全量列表
 */
export function enrichRolePermissionsWithAncestors(roleNodes, flatAll) {
  if (!Array.isArray(roleNodes) || !roleNodes.length) return []
  if (!Array.isArray(flatAll) || !flatAll.length) return roleNodes
  const byId = new Map(flatAll.map((n) => [refKey(n.id), n]))
  const out = new Map()
  for (const n of roleNodes) {
    const k = refKey(n.id)
    if (k) out.set(k, n)
  }
  for (const n of roleNodes) {
    let pid = refKey(n.parentId)
    let guard = 0
    while (pid && pid !== '0' && guard++ < 100) {
      if (out.has(pid)) break
      const parent = byId.get(pid)
      if (!parent) break
      out.set(pid, parent)
      pid = refKey(parent.parentId)
    }
  }
  return Array.from(out.values())
}

/** 角色行内已选权限 id 列表（与 Tree checkedKeys 一致，string） */
export function getRolePermissionCheckedKeys(row) {
  return pickRolePermissionNodes(row).map((n) => String(n.id))
}

/**
 * 三层树：根 → 子 → 孙（孙级用 Tag）。
 * id / parentId 按字符串统一比较。
 *
 * @returns {Array<{
 *   key: number|string,
 *   root: object,
 *   level2: Array<{ node: object, level3: object[] }>
 * }>}
 */
export function groupPermissionNodesThreeLevels(nodes) {
  if (!Array.isArray(nodes) || !nodes.length) return []

  const idSet = new Set(nodes.map((n) => refKey(n.id)).filter((k) => k != null))
  const childrenByParent = new Map()

  for (const n of nodes) {
    if (isRootNode(n, idSet)) continue
    const pKey = refKey(n.parentId)
    if (!pKey || pKey === '0') continue
    if (!childrenByParent.has(pKey)) childrenByParent.set(pKey, [])
    childrenByParent.get(pKey).push(n)
  }

  const roots = nodes.filter((n) => isRootNode(n, idSet))

  return roots.map((root) => {
    const rk = refKey(root.id)
    const l2list = (rk && childrenByParent.get(rk)) || []
    return {
      key: root.id,
      root,
      level2: l2list.map((l2) => ({
        node: l2,
        level3: childrenByParent.get(refKey(l2.id)) || []
      }))
    }
  })
}

/**
 * 全量权限平铺 → Ant Design Tree 的 treeData（按 id / parentId 建树）
 */
export function buildPermissionAntTreeData(nodes) {
  if (!Array.isArray(nodes) || !nodes.length) return []
  const idSet = new Set(nodes.map((n) => refKey(n.id)).filter((k) => k != null))
  const childrenByParent = new Map()

  for (const n of nodes) {
    if (isRootNode(n, idSet)) continue
    const pKey = refKey(n.parentId)
    if (!pKey || pKey === '0') continue
    if (!childrenByParent.has(pKey)) childrenByParent.set(pKey, [])
    childrenByParent.get(pKey).push(n)
  }

  const roots = nodes.filter((n) => isRootNode(n, idSet))

  const toTreeItem = (n) => {
    const key = refKey(n.id)
    const children = (childrenByParent.get(key) || []).map(toTreeItem)
    const title = n.code ? `${n.name} (${n.code})` : n.name
    return {
      title,
      key,
      children: children.length ? children : undefined
    }
  }

  return roots.map(toTreeItem)
}

/** 收集树节点 key（用于全选） */
export function collectAllTreeKeys(treeData) {
  const keys = []
  const walk = (arr) => {
    if (!Array.isArray(arr)) return
    arr.forEach((node) => {
      if (node.key != null) keys.push(node.key)
      if (node.children && node.children.length) walk(node.children)
    })
  }
  walk(treeData)
  return keys
}

/**
 * Ant Design Tree 的 treeData（含 key）→ 子 key → 父 key（仅一层）。
 * 须基于**全量**树构建，保证搜索过滤后仍能解析祖先链。
 */
export function buildAntTreeKeyParentMap(treeData) {
  const map = new Map()
  const walk = (nodes, parentKey) => {
    if (!Array.isArray(nodes)) return
    for (const node of nodes) {
      const k = node.key != null ? String(node.key) : null
      if (k && parentKey != null) {
        map.set(k, String(parentKey))
      }
      if (node.children && node.children.length) {
        walk(node.children, k ?? parentKey)
      }
    }
  }
  walk(treeData, null)
  return map
}

/**
 * Ant Design Tree treeData → 父 key → 直接子 key 列表（用于「无子选中则去父」剪枝）
 */
export function buildAntTreeChildrenMap(treeData) {
  const map = new Map()
  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return
    for (const node of nodes) {
      const k = node.key != null ? String(node.key) : null
      if (!k) continue
      if (node.children && node.children.length) {
        const childKeys = node.children
          .map((c) => (c.key != null ? String(c.key) : null))
          .filter(Boolean)
        map.set(k, childKeys)
        walk(node.children)
      }
    }
  }
  walk(treeData)
  return map
}

/**
 * 勾选集合补上所有祖先 key（子级勾选时父级一并提交）。
 * @param {Array<string|number>} keys Tree checkedKeys
 * @param {Map<string, string>} parentMap buildAntTreeKeyParentMap 结果
 */
export function mergeCheckedKeysWithAncestors(keys, parentMap) {
  if (!Array.isArray(keys) || !keys.length) return keys || []
  const set = new Set(keys.map((k) => String(k)))
  for (const k of [...set]) {
    let p = parentMap.get(k)
    let guard = 0
    while (p && guard++ < 200) {
      set.add(p)
      p = parentMap.get(p)
    }
  }
  return Array.from(set)
}

/**
 * 去掉在树中有子节点、但集合里已无任何**后代**被选中的 key（取消全部子级后父级一并取消；叶子节点保留）。
 * @param {Array<string|number>} keys 已合并祖先后的 checkedKeys
 * @param {Array} treeData 全量 treeData
 */
export function pruneCheckedKeysWithoutDescendants(keys, treeData) {
  if (!Array.isArray(keys) || !keys.length) return keys || []
  if (!Array.isArray(treeData) || !treeData.length) return keys
  const childrenByParent = buildAntTreeChildrenMap(treeData)
  const keySet = new Set(keys.map((k) => String(k)))

  const hasStrictDescendantInSet = (rootKey) => {
    const stack = [...(childrenByParent.get(rootKey) || [])]
    const seen = new Set()
    while (stack.length) {
      const c = stack.pop()
      if (seen.has(c)) continue
      seen.add(c)
      if (keySet.has(c)) return true
      const more = childrenByParent.get(c)
      if (more) {
        for (const m of more) stack.push(m)
      }
    }
    return false
  }

  let changed = true
  while (changed) {
    changed = false
    for (const k of [...keySet]) {
      const kids = childrenByParent.get(k)
      if (!kids || kids.length === 0) continue
      if (!hasStrictDescendantInSet(k)) {
        keySet.delete(k)
        changed = true
      }
    }
  }
  return Array.from(keySet)
}

/**
 * 回填表单/树勾选：角色接口可能只返回叶子权限 id，按全量权限树补上祖先 id，与 PermissionTreeField 勾选子级时合并父级一致。
 * @param {object} row 角色行
 * @param {Array} treeData `buildPermissionAntTreeData(permissionsList)` 结果
 */
export function getRolePermissionCheckedKeysWithAncestors(row, treeData) {
  const raw = getRolePermissionCheckedKeys(row)
  if (!Array.isArray(raw) || !raw.length) return raw
  if (!Array.isArray(treeData) || !treeData.length) return raw
  return mergeCheckedKeysWithAncestors(raw, buildAntTreeKeyParentMap(treeData))
}

/**
 * 按标题关键字过滤树（不区分大小写），保留命中节点及其祖先链
 */
export function filterPermissionTreeData(treeData, keyword) {
  if (!keyword || !String(keyword).trim()) return treeData
  const lower = String(keyword).trim().toLowerCase()
  const filter = (nodes) => {
    if (!Array.isArray(nodes)) return []
    return nodes.reduce((acc, node) => {
      const titleStr = typeof node.title === 'string' ? node.title : ''
      const childFiltered = node.children ? filter(node.children) : []
      const match = titleStr.toLowerCase().includes(lower)
      if (match || childFiltered.length) {
        acc.push({
          ...node,
          children: childFiltered.length ? childFiltered : node.children
        })
      }
      return acc
    }, [])
  }
  return filter(treeData)
}

/**
 * 平铺权限列表 → Ant Design TreeSelect 的 treeData（value + title）
 * @param {Array<object>} nodes
 * @param {{ maxLevels?: number }} [options] 从根算起保留的层数（根为第 1 层）；不传则展开全树
 */
export function buildPermissionTreeSelectData(nodes, options = {}) {
  if (!Array.isArray(nodes) || !nodes.length) return []
  const { maxLevels } = options
  const idSet = new Set(nodes.map((n) => refKey(n.id)).filter((k) => k != null))
  const childrenByParent = new Map()

  for (const n of nodes) {
    if (isRootNode(n, idSet)) continue
    const pKey = refKey(n.parentId)
    if (!pKey || pKey === '0') continue
    if (!childrenByParent.has(pKey)) childrenByParent.set(pKey, [])
    childrenByParent.get(pKey).push(n)
  }

  const roots = nodes.filter((n) => isRootNode(n, idSet))

  const toItem = (n, level = 1) => {
    const key = refKey(n.id)
    const rawKids = childrenByParent.get(key) || []
    const children =
      maxLevels == null || level < maxLevels
        ? rawKids.map((c) => toItem(c, level + 1))
        : []
    const title = n.code ? `${n.name}（${n.code}）` : n.name
    return {
      title,
      value: key,
      children: children.length ? children : undefined
    }
  }

  return roots.map((r) => toItem(r, 1))
}

/**
 * 子权限编码前缀（与「父级 code + 子级后缀」拼接后提交）。
 * - 父级 code 已含「:」时视为已存整段路径，前缀为「父级 code + ':'」。
 * - 否则视为每级仅存末段，自根向下拼到父级：「ancestor1:ancestor2:...:」。
 * @param {Array<object>} nodes 平铺权限列表
 * @param {string|number|null|undefined} parentId
 * @returns {string}
 */
export function getPermissionParentCodePrefix(nodes, parentId) {
  if (parentId == null || parentId === '') return ''
  const list = nodes || []
  const byId = new Map(list.map((n) => [refKey(n.id), n]))
  let cur = byId.get(refKey(parentId))
  if (!cur) return ''
  const immCode = String(cur.code || '').trim()
  if (!immCode) return ''
  if (immCode.includes(':')) {
    return `${immCode}:`
  }
  const segments = []
  let guard = 0
  while (cur && guard++ < 100) {
    const c = String(cur.code || '').trim()
    if (c) segments.unshift(c)
    const pid = refKey(cur.parentId)
    if (!pid || pid === '0') break
    cur = byId.get(pid)
  }
  if (!segments.length) return ''
  return `${segments.join(':')}:`
}

/**
 * 平铺 → Table 树形 dataSource（保留行字段 + children）
 */
export function buildPermissionTreeForTable(nodes) {
  if (!Array.isArray(nodes) || !nodes.length) return []
  const idSet = new Set(nodes.map((n) => refKey(n.id)).filter((k) => k != null))
  const childrenByParent = new Map()

  for (const n of nodes) {
    if (isRootNode(n, idSet)) continue
    const pKey = refKey(n.parentId)
    if (!pKey || pKey === '0') continue
    if (!childrenByParent.has(pKey)) childrenByParent.set(pKey, [])
    childrenByParent.get(pKey).push(n)
  }

  const roots = nodes.filter((n) => isRootNode(n, idSet))

  const toRow = (n) => {
    const key = refKey(n.id)
    const rawChildren = childrenByParent.get(key) || []
    const sorted = [...rawChildren].sort((a, b) => {
      const idA = Number(a.id)
      const idB = Number(b.id)
      if (!Number.isNaN(idA) && !Number.isNaN(idB)) return idA - idB
      return String(a.id).localeCompare(String(b.id))
    })
    const childRows = sorted.map(toRow)
    return {
      ...n,
      children: childRows.length ? childRows : undefined
    }
  }

  return roots.map(toRow)
}

/**
 * 平铺（可先 filter）→ Ant Design Tree 的 treeData，节点上挂 `record` 供详情/交互使用
 */
export function buildPermissionTreeNodesForAntTree(flatList) {
  const treeRows = buildPermissionTreeForTable(flatList)
  const mapRow = (r) => ({
    key: String(r.id),
    title: r.name,
    record: r,
    children: r.children ? r.children.map(mapRow) : undefined
  })
  return treeRows.map(mapRow)
}

/**
 * 关键字过滤平铺列表：命中节点 + 祖先 + 子树，再用于建树
 */
export function filterPermissionsByKeyword(flatList, keyword) {
  if (!flatList || !flatList.length) return []
  if (!keyword || !String(keyword).trim()) return flatList
  const k = String(keyword).trim().toLowerCase()
  const byId = new Map(flatList.map((n) => [refKey(n.id), n]))
  const match = (n) => {
    const name = String(n.name || '').toLowerCase()
    const code = String(n.code || '').toLowerCase()
    const type = String(n.type || '').toLowerCase()
    return name.includes(k) || code.includes(k) || type.includes(k)
  }
  const seed = new Set()
  for (const n of flatList) {
    if (match(n)) seed.add(refKey(n.id))
  }
  const keep = new Set(seed)
  for (const id of seed) {
    let cur = byId.get(id)
    while (cur) {
      const pid = refKey(cur.parentId)
      if (!pid || pid === '0') break
      keep.add(pid)
      cur = byId.get(pid)
    }
  }
  const childrenByParent = new Map()
  for (const n of flatList) {
    const pk = refKey(n.parentId)
    if (!pk || pk === '0') continue
    if (!childrenByParent.has(pk)) childrenByParent.set(pk, [])
    childrenByParent.get(pk).push(n)
  }
  const stack = [...seed]
  while (stack.length) {
    const id = stack.pop()
    const ch = childrenByParent.get(id) || []
    for (const c of ch) {
      const ck = refKey(c.id)
      if (!keep.has(ck)) {
        keep.add(ck)
        stack.push(ck)
      }
    }
  }
  return flatList.filter((n) => keep.has(refKey(n.id)))
}

/**
 * 收集某节点下全部后代 id（含多级），用于编辑时禁止将父级选为自己或子孙
 */
export function collectPermissionDescendantIds(flatList, rootId) {
  if (!flatList || !flatList.length || rootId == null) return new Set()
  const childrenByParent = new Map()
  for (const n of flatList) {
    const pk = refKey(n.parentId)
    if (!pk || pk === '0') continue
    if (!childrenByParent.has(pk)) childrenByParent.set(pk, [])
    childrenByParent.get(pk).push(n)
  }
  const out = new Set()
  const walk = (id) => {
    const ch = childrenByParent.get(refKey(id)) || []
    for (const c of ch) {
      const ck = refKey(c.id)
      out.add(ck)
      walk(c.id)
    }
  }
  walk(rootId)
  return out
}

/**
 * 当前节点在树中的层级深度（顶级为 1，子级 +1）。用于限制最多三层等业务规则。
 * @param {Array<object>} flatList 全量平铺权限列表
 * @param {object} record 当前节点（含 id、parentId）
 * @returns {number}
 */
export function getPermissionDepthInTree(flatList, record) {
  if (!record || !flatList || !flatList.length) return 1
  const byId = new Map(flatList.map((n) => [refKey(n.id), n]))
  let ancestors = 0
  let cur = record
  const seen = new Set()
  while (cur) {
    const idStr = refKey(cur.id)
    if (idStr && seen.has(idStr)) break
    if (idStr) seen.add(idStr)
    const pid = refKey(cur.parentId)
    if (!pid || pid === '0') break
    const parent = byId.get(pid)
    if (!parent) break
    ancestors += 1
    cur = parent
  }
  return ancestors + 1
}
